export type ExportSide = 'merged' | 'yes' | 'no';
export type ExportData = 'candles' | 'prices' | 'clock';
export type ExportFile = 'csv' | 'json';

export interface ExportEventRef {
    id: string;
    slug: string;
    title: string;
}

export interface ExportMarket {
    conditionId: string;
    label: string;
    fromMs?: number;
    toMs?: number;
    event?: ExportEventRef;
}

export interface ExportSettings {
    eventId: string;
    eventSlug: string;
    eventTitle: string;
    markets: readonly ExportMarket[];
    fromMs: number;
    toMs: number;
    intervalMinutes: number;
    side: ExportSide;
    data: ExportData;
    file: ExportFile;
    fill: boolean;
}

export interface Candle {
    t: number;
    open: number;
    high: number;
    low: number;
    close: number;
    values: number[];
}

export type RawRow = Record<string, unknown>;

export interface PageRequest {
    conditionId: string;
    side: ExportSide;
    startMs: number;
    endMs: number;
    intervalMinutes: number;
}

export interface ClockExportRequest {
    markets: readonly ExportMarket[];
    fromMs: number;
    toMs: number;
    eventSlug: string;
    eventTitle: string;
    signal: AbortSignal;
    onProgress: (progress: { done: number; total: number }) => void;
    /** The user has seen the size and said yes (see ClockExportConfirm). */
    allowLarge?: boolean;
}

/**
 * What the clock download throws, instead of loading anything, when the user
 * should confirm its size first. Asking again with `allowLarge: true` goes
 * ahead. The box tests for it with isClockExportConfirm and so needs nothing
 * from the clock folder.
 */
export interface ClockExportConfirm {
    code: 'confirm-large';
    megabytes: number;
}

export function isClockExportConfirm(
    error: unknown
): error is Error & ClockExportConfirm {
    return (
        typeof error === 'object' &&
        error !== null &&
        (error as { code?: unknown }).code === 'confirm-large' &&
        typeof (error as { megabytes?: unknown }).megabytes === 'number'
    );
}

/** Builds the Event clock download. Passed to ExportBox only for researchers. */
export type ClockExportFn = (
    request: ClockExportRequest
) => Promise<{ filename: string; blob: Blob; rows: number }>;

export const CLOCK_EXPORT_HINT =
    'The event clock for each outcome: the prices it used, the clock over time, the results and a Python script that repeats the run with eventclock-py. Follows your Event clock settings.';
